import { useCurrentUser, useUserAuthState, useUserBaseUrl } from '@cosuno/authentication-frontend';
import type React from 'react';
import { memo, type PropsWithChildren, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useMatches, useNavigate } from 'react-router-dom';

import { USER_TYPE } from '~/__gql__/graphql';
import { useAgentSignupRedirectRoute } from '~/shared/hooks/useAgentSignupRedirectRoute';

import { AuthHandlerContext } from './context';
import { checkIfRouteIsAllowed } from './utils';

const AuthHandler: React.FC<PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate();
  const matches = useMatches();
  const { pathname, search } = useLocation();

  const user = useCurrentUser();
  const { isUserAnonymous, isUserAgent, isUserPrincipal } = useUserAuthState();
  const userBaseUrl = useUserBaseUrl();
  const agentSignupRedirectRoute = useAgentSignupRedirectRoute();

  const isAdmin = user?.type === USER_TYPE.administrator;

  const routeIsAllowed = useMemo(
    () =>
      checkIfRouteIsAllowed(
        matches,
        { isUserAnonymous, isUserPrincipal, isUserAgent },
        { isAdmin },
      ),
    [isAdmin, isUserAgent, isUserAnonymous, isUserPrincipal, matches],
  );

  const redirectToBaseUrl = useCallback(() => {
    navigate(
      {
        pathname: agentSignupRedirectRoute?.pathname ?? userBaseUrl,
        search: agentSignupRedirectRoute?.search,
      },
      {
        state: { redirect: `${pathname}${search ?? ''}` },
        replace: true,
      },
    );
  }, [navigate, agentSignupRedirectRoute, userBaseUrl, pathname, search]);

  const handleUnauthorized = useCallback((): (() => void) => {
    const ref = setTimeout(() => {
      redirectToBaseUrl();
    }, 500);

    return () => {
      clearTimeout(ref);
    };
  }, [redirectToBaseUrl]);

  // If the route is rendered but `mountRoute` is false for 500
  // milliseconds, redirect to the user type's base url.
  // This allows useAuthentication.logout to do its own route change, before the redirect here
  // takes effect.
  useEffect(() => {
    if (!routeIsAllowed) return handleUnauthorized();
    return undefined;
  }, [routeIsAllowed, handleUnauthorized]);

  const ctxValue = useMemo(() => ({ handleUnauthorized }), [handleUnauthorized]);

  if (!routeIsAllowed) return null;

  return <AuthHandlerContext.Provider value={ctxValue}>{children}</AuthHandlerContext.Provider>;
};

export default memo(AuthHandler);
